<script setup lang="ts">
import type { MenuItem } from 'primevue/menuitem/MenuItem'
import { computed, PropType, ref } from 'vue'
import OpensInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import { useUserStore } from '@/authentication/user.store'
import PremiumActionModal from '@/components/PremiumActionModal.vue'

defineProps({
  lists: {
    type: Array as PropType<Array<MenuItem>>,
    default: () => []
  },
  title: {
    type: String,
    required: true,
    default: 'Menu'
  }
})

const showSubmenu = ref(false)
const toggleSubmenu = () => {
  showSubmenu.value = !showSubmenu.value
}

const userStore = useUserStore()
const isAuthenticated = computed(() => userStore.isAuthenticated)
const isPremium = computed(() => userStore.isPremium)
const isLicenseExpired = computed(() => userStore.isLicenseExpired)
const showPremiumActionModal = ref(false)
const openModal = () => {
  showPremiumActionModal.value = true
}

const premiumMessage =
  'To access this feature, you must have an active membership through your school or district. Contact your district administrator to purchase or renew your premium membership.'

const preventNavigation = computed(() => (isPremiumRoute: boolean) => {
  if (userStore.fetching) {
    return false
  }
  if (!isAuthenticated.value || isLicenseExpired.value) {
    return true
  }
  if (isPremiumRoute && !isPremium.value) {
    return true
  }
  return false
})
</script>

<template>
  <div class="bg-surface-0 rounded-xs py-1">
    <h2 class="px-3 text-text-2xl font-bold leading-8">{{ title }}</h2>
    <template v-for="(list, index) in lists" :key="list.heading">
      <h3
        v-if="list.heading"
        :id="`sidebar-menu-heading-${index}`"
        class="px-3 pb-2 pt-5 font-medium leading-6"
      >
        {{ list.heading }}
      </h3>
      <ul
        role="menu"
        tabindex="0"
        class="m-0 max-w-40 list-none p-0 outline-none"
        :aria-labelledby="list.heading ? `sidebar-menu-heading-${index}` : undefined"
        :aria-label="list.heading ? undefined : title"
      >
        <li
          v-for="item in list.items"
          class="relative my-0.5 first:mt-0"
          :key="typeof item.label === 'function' ? item.label() : item.label"
          :ariaCurrent="item.selected ? 'page' : ''"
          role="menuitem"
        >
          <template v-if="item.route">
            <div
              class="relative flex cursor-pointer select-none items-center overflow-hidden rounded-xs px-3 py-2 text-primary-400 no-underline transition-shadow duration-200 focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring-2 focus-visible:ring-gray-600"
              :class="
                item.selected
                  ? 'bg-primary-400 text-white hover:bg-primary-400 hover:text-white'
                  : 'hover:bg-surface-300 hover:text-primary-500'
              "
            >
              <span
                v-if="item.submenu"
                class="material-icons-round mr-1 rounded-xs transition-colors duration-200 hover:bg-primary-600"
                @click.stop="toggleSubmenu()"
                >arrow_drop_down</span
              >
              <button
                v-if="preventNavigation(item.isPremiumRoute)"
                class="w-full text-left text-md leading-normal"
                @click="openModal"
              >
                {{ item.label }}
              </button>
              <router-link v-else :to="item.route" custom v-slot="{ href, navigate }">
                <a :href="href" @click="navigate" class="w-full">
                  <span class="text-md leading-normal">{{ item.label }}</span>
                </a>
              </router-link>
            </div>
          </template>
          <template v-else>
            <div
              class="relative flex cursor-pointer select-none items-center overflow-hidden rounded-xs px-3 py-2 text-primary-400 no-underline transition-shadow duration-200 focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring-2 focus-visible:ring-gray-600"
              :class="
                item.selected
                  ? 'bg-primary-400 text-white hover:bg-primary-400 hover:text-white'
                  : 'hover:bg-surface-300 hover:text-primary-500'
              "
            >
              <span
                v-if="item.submenu"
                class="material-icons-round mr-1 rounded-xs transition-colors duration-200 hover:bg-primary-600"
                @click.stop="toggleSubmenu()"
                >arrow_drop_down</span
              >
              <button
                v-if="preventNavigation(item.isPremiumRoute)"
                class="w-full text-left text-md leading-normal"
                @click="openModal"
              >
                {{ item.label }}
              </button>
              <a
                v-else
                :href="item.url"
                :target="item.target"
                :rel="item.target === '_blank' ? 'noopener noreferrer' : undefined"
                class="flex w-full items-center"
                @click="item.onClick"
              >
                <span class="mr-1 text-md leading-normal">{{ item.label }}</span>
                <OpensInNewTabIcon v-if="item.target === '_blank'" :external-link="true" />
              </a>
            </div>
          </template>
          <ul v-if="item.submenu && showSubmenu" role="menu" class="ml-4 mt-1">
            <li
              v-for="subitem in item.submenu"
              :key="typeof subitem.label === 'function' ? subitem.label() : subitem.label"
              class="relative my-0.5 first:mt-0"
              :ariaCurrent="subitem.selected ? 'page' : ''"
              role="menuitem"
            >
              <template v-if="preventNavigation(subitem.isPremiumRoute ?? item.isPremiumRoute)">
                <button
                  class="relative flex w-full cursor-pointer select-none overflow-hidden rounded-xs px-3 py-2 text-primary-400 no-underline transition-shadow duration-200 focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring-2 focus-visible:ring-gray-600 hover:bg-surface-300 hover:text-primary-500 text-md leading-normal"
                  @click="openModal"
                >
                  {{ subitem.label }}
                </button>
              </template>
              <template v-else-if="subitem.route">
                <router-link :to="subitem.route" custom v-slot="{ href, navigate }">
                  <a
                    :href="href"
                    @click="navigate"
                    class="relative flex cursor-pointer select-none overflow-hidden rounded-xs px-3 py-2 text-primary-400 no-underline transition-shadow duration-200 focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring-2 focus-visible:ring-gray-600"
                    :class="
                      subitem.selected
                        ? 'bg-primary-400 text-white hover:bg-primary-400 hover:text-white'
                        : 'hover:bg-surface-300 hover:text-primary-500'
                    "
                  >
                    <span class="text-md leading-normal">{{ subitem.label }}</span>
                  </a>
                </router-link>
              </template>
              <template v-else>
                <a
                  :href="subitem.url"
                  :target="subitem.target"
                  :rel="subitem.target === '_blank' ? 'noopener noreferrer' : undefined"
                  class="relative flex cursor-pointer select-none items-center overflow-hidden rounded-xs px-3 py-2 text-primary-400 no-underline transition-shadow duration-200 focus-visible:outline-none focus-visible:outline-offset-0 focus-visible:ring-2 focus-visible:ring-gray-600"
                  :class="
                    subitem.selected
                      ? 'bg-primary-400 text-white hover:bg-primary-400 hover:text-white'
                      : 'hover:bg-surface-300 hover:text-primary-500'
                  "
                >
                  <span class="mr-1 text-md leading-normal">{{ subitem.label }}</span>
                  <OpensInNewTabIcon v-if="subitem.target === '_blank'" :external-link="true" />
                </a>
              </template>
            </li>
          </ul>
        </li>
      </ul>
    </template>
    <PremiumActionModal v-model:external-open="showPremiumActionModal" :body-message="premiumMessage" />
  </div>
</template>
